/**
 * TableLinks module provides a collection of navigation button components
 * for the horse profile table. These buttons link to related pages such as
 * racecourses, profiles, race results, replays, and racecards.
 */
import { Platform } from "react-native";
import { cleanSlug } from "../../../../../utils/cleanSlug";
import RTouchableOpacity from "../../../../RLink/RTouchableOpacity";
import RText from "../../../../RText";
import {
  findHorseSlugByName,
  findJockeySlugByName,
  findTrainerSlugByName,
} from "../../components/Utils";
import { prepareParams } from "../../../Results/utils";
import { formatDateWithoutDay } from "../../../../../utils/TimeCalculator";
import VideoLink from "../../../../VideoLink/VideoLink";
import { Performance } from "../../../../../types/races/horse";
import { getRegion } from "../../../../../utils/country";

/**
 * Renders a button that links to a racecourse profile page.
 *
 * @param {Object} props - The component props
 * @param {Object} props.track - The track information object
 * @param {string} props.track.country_code.iso - Country code for the track
 * @param {string} props.track.slug - URL slug for the track
 * @param {string} props.track.name_3_char - Short name/code for the track
 * @returns {JSX.Element} A button linked to the racecourse page
 */
export const CourseButton = ({ track }) => {
  const screen = "Racecourse";
  const params = {
    region: getRegion(track.country_code.iso),
    track: cleanSlug(track.slug),
  };

  return (
    <RTouchableOpacity
      style={{
        minWidth: 100,
        flex: 1,
        paddingTop: Platform.OS === "ios" ? 3 : 0,
      }}
      navScreen={screen}
      navParams={params}
      shouldOpenInNewTab
    >
      <RText weight="semiBold" color="secondary">
        {track.name_3_char}
      </RText>
    </RTouchableOpacity>
  );
};

/**
 * Renders a button that links to a profile page for a jockey, horse, or trainer.
 * Uses the appropriate method to find the slug based on the profile type.
 *
 * @param {Object} props - The component props
 * @param {string} props.type - The profile type ('jockey', 'horse', or 'trainer')
 * @param {string} props.name - The name of the person or horse
 * @param {Performance[]} props.formRecords - Array of form records to search for the profile
 * @returns {JSX.Element} A button linked to the profile page
 */
export const ProfileButton = ({
  type,
  name,
  formRecords,
}: {
  type: string;
  name: string;
  formRecords: Performance[];
}) => {
  const methodMap = {
    jockey: findJockeySlugByName,
    horse: findHorseSlugByName,
    trainer: findTrainerSlugByName,
  };

  return (
    <RTouchableOpacity
      navScreen="IndexOrProfile"
      navParams={{
        type: type,
        slug: cleanSlug(methodMap[type](formRecords, name)),
      }}
      style={{
        minWidth: 100,
        flex: 1,
        paddingTop: Platform.OS === "ios" ? 3 : 0,
      }}
      shouldOpenInNewTab
    >
      <RText weight="semiBold" color="secondary">
        {name}
      </RText>
    </RTouchableOpacity>
  );
};

/**
 * Renders a button that links to a race result page for a specific date.
 *
 * @param {Object} props - The component props
 * @param {Object} props.data - Race data containing date and track information
 * @param {string} [props.navScreen="IndividualResult"] - Navigation screen name
 * @returns {JSX.Element} A button linked to the race result page
 */
export const DateButton = ({ data, navScreen = "IndividualResult" }) => {
  const params = prepareParams({
    date: data?.race?.meeting?.date,
    startTime: data.race_start_time_scheduled || data.race.start_time_scheduled,
    track: cleanSlug(data?.track?.slug || data.race.meeting.track.slug),
  });
  return (
    <RTouchableOpacity
      style={{
        minWidth: 100,
        flex: 1,
        paddingTop: Platform.OS === "ios" ? 3 : 0,
      }}
      navScreen={navScreen}
      navParams={params}
      shouldOpenInNewTab
    >
      <RText weight="semiBold" color="secondary">
        {formatDateWithoutDay(data.meeting_date || data.race.meeting.date)}
      </RText>
    </RTouchableOpacity>
  );
};

/**
 * Renders a video link button for watching race replays.
 * If no replay video is available, displays a dash.
 *
 * @param {Object} props - The component props
 * @param {Object} props.data - Race data containing replay video information
 * @returns {JSX.Element} A video link button or placeholder text
 */
export const ReplayButton = ({ data }) => {
  const replayVid = data?.race?.replay_videos[0];
  if (!replayVid) return <RText style={{ color: "transparent" }}>-</RText>;
  return (
    <VideoLink
      race={data.race}
      videoType={"replay"}
      displayIconOnly
      // This won't be true on Left Hand Side until backend update made
      // but better to err on the side of true for now.
      shouldBecomeAvailable={
        data?.race?.meeting?.track?.tv_channel == "RTV" || true
      }
      trackSlug={data?.track?.slug}
      start_time_scheduled={data.race_start_time_scheduled}
      iconSize="medium"
    ></VideoLink>
  );
};

/**
 * Renders a button that links to a specific race card.
 *
 * @param {Object} props - The component props
 * @param {Object} props.race - Race data containing title and scheduling information
 * @returns {JSX.Element} A button linked to the individual racecard page
 */
export const RaceButton = ({ race }) => {
  const params = prepareParams({
    startTime: race.start_time_scheduled,
    track: race.meeting.track.slug,
  });
  return (
    <RTouchableOpacity
      style={{
        minWidth: 100,
        flex: 1,
        paddingTop: Platform.OS === "ios" ? 3 : 0,
      }}
      navScreen="IndividualRacecard"
      navParams={params}
      shouldOpenInNewTab
    >
      <RText weight="semiBold" color="secondary">
        {race.title?.capitalize()}
      </RText>
    </RTouchableOpacity>
  );
};
